import { useState, useEffect } from "react";
import { format } from "date-fns";

// type Frequency = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";
type EndType = "NEVER" | "ON_DATE" | "AFTER_OCCURRENCES";

export interface RecurrenceRule {
  frequency: string;
  interval: number;
  weekDays: string[]; // e.g., ['MO', 'WE']
  endType: EndType;
  endDate: Date | null;
  occurrences: number;
}

interface CustomRecurrenceModalProps {
  onSave: (rule: RecurrenceRule) => void;
  initialRule: {
    [Property in keyof RecurrenceRule]?: RecurrenceRule[Property];
  };
  disabled?: boolean;
}

const DAYS_OF_WEEK = [
  { label: "M", value: "MO" },
  { label: "T", value: "TU" },
  { label: "W", value: "WE" },
  { label: "T", value: "TH" },
  { label: "F", value: "FR" },
  { label: "S", value: "SA" },
];

const FREQUENCIES: { label: string; value: string }[] = [
  { label: "day", value: "DAILY" },
  { label: "week", value: "WEEKLY" },
  { label: "month", value: "MONTHLY" },
  { label: "year", value: "YEARLY" },
];

export const SnapshotEventReminderForm = ({ onSave, initialRule, disabled = false }: CustomRecurrenceModalProps) => {
  const [frequency, setFrequency] = useState<string>(initialRule?.frequency ?? "WEEKLY");
  const [interval, setInterval] = useState<number>(initialRule?.interval ?? 1);
  const [weekDays, setWeekDays] = useState<string[]>(initialRule?.weekDays ?? ["MO"]); // Default to Monday
  const [endType, setEndType] = useState<EndType>(initialRule?.endType ?? "NEVER");
  const [endDate, setEndDate] = useState<Date>(initialRule?.endDate ?? new Date());
  const [occurrences, setOccurrences] = useState<number>(initialRule?.occurrences ?? 13);

  useEffect(() => {
    const rule: RecurrenceRule = {
      frequency,
      interval,
      weekDays: frequency === "WEEKLY" ? weekDays : [],
      endType,
      endDate: endType === "ON_DATE" ? endDate : null,
      occurrences: endType === "AFTER_OCCURRENCES" ? occurrences : 0,
    };

    onSave?.(rule);
  }, [frequency, interval, weekDays, endType, endDate, occurrences, onSave]);

  // --- Handlers ---
  const toggleWeekDay = (day: string) => {
    setWeekDays(prev => (prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]));
  };

  return (
    <div className="w-full overflow-hidden rounded-lg bg-ivory-mist-200 text-gray-800">
      {/* Body */}
      <div className="space-y-6 px-6 py-4">
        {/* Repeat Every Section */}
        <div className="flex items-center space-x-3">
          <span className="w-24 text-sm font-medium text-gray-700">Repeat every</span>
          <input
            type="number"
            min="1"
            max="99"
            disabled={disabled}
            value={interval}
            onChange={e => setInterval(Math.max(1, parseInt(e.target.value) || 1))}
            className="w-16 rounded-md border border-gray-300 p-2 text-center outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
          />
          <div className="relative flex-1">
            <select
              value={frequency}
              disabled={disabled}
              onChange={e => setFrequency(e.target.value)}
              className="w-full cursor-pointer appearance-none rounded-md border border-gray-300 bg-gray-50 p-2 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            >
              {FREQUENCIES.map(freq => (
                <option key={freq.value} value={freq.value}>
                  {freq.label}
                  {interval > 1 ? "s" : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Repeat On (Days of Week) - Only for Weekly */}
        {frequency === "WEEKLY" && (
          <div className="space-y-2">
            <span className="text-sm font-medium text-gray-700">Repeat on</span>
            <div className="flex justify-between">
              {DAYS_OF_WEEK.map(day => {
                const isSelected = weekDays.includes(day.value);
                return (
                  <button
                    key={day.value}
                    disabled={disabled}
                    type="button"
                    onClick={() => toggleWeekDay(day.value)}
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium transition-all ${
                      isSelected
                        ? "scale-105 transform bg-primary text-white shadow-md"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    } `}
                    title={day.value}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Ends Section */}
        <div className="space-y-3">
          <span className="block text-sm font-medium text-gray-700">Ends</span>

          <div className="space-y-3">
            {/* Option: Never */}
            <label className="group flex cursor-pointer items-center space-x-3">
              <div className="relative flex items-center">
                <input
                  type="radio"
                  name="endType"
                  disabled={disabled}
                  checked={endType === "NEVER"}
                  onChange={() => setEndType("NEVER")}
                  className="peer h-4 w-4 cursor-pointer border-gray-300 text-primary focus:ring-primary-foreground"
                />
              </div>
              <span className="text-sm text-gray-700 group-hover:text-gray-900">Never</span>
            </label>

            {/* Option: On Date */}
            <label className="group flex cursor-pointer items-center space-x-3">
              <input
                type="radio"
                name="endType"
                checked={endType === "ON_DATE"}
                disabled={disabled}
                onChange={() => setEndType("ON_DATE")}
                className="h-4 w-4 cursor-pointer border-gray-300 text-primary focus:ring-primary-foreground"
              />
              <span className="w-16 text-sm text-gray-700">On</span>
              <div
                className={`relative flex items-center ${endType !== "ON_DATE" ? "pointer-events-none opacity-50" : ""}`}
              >
                <input
                  type="date"
                  value={format(endDate, "yyyy-MM-dd")}
                  onChange={e => setEndDate(e.target.valueAsDate || new Date())}
                  disabled={disabled}
                  className="rounded-md border border-gray-300 p-1.5 pl-2 text-sm text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-primary-foreground"
                />
              </div>
            </label>

            {/* Option: After Occurrences */}
            <label className="group flex cursor-pointer items-center space-x-3">
              <input
                type="radio"
                name="endType"
                disabled={disabled}
                checked={endType === "AFTER_OCCURRENCES"}
                onChange={() => setEndType("AFTER_OCCURRENCES")}
                className="h-4 w-4 cursor-pointer border-gray-300 text-primary focus:ring-primary-foreground"
              />
              <span className="w-16 text-sm text-gray-700">After</span>
              <div
                className={`flex items-center space-x-2 ${endType !== "AFTER_OCCURRENCES" ? "pointer-events-none opacity-50" : ""}`}
              >
                <input
                  type="number"
                  min="0"
                  disabled={disabled}
                  value={occurrences}
                  onChange={e => setOccurrences(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-16 rounded-md border border-gray-300 p-1.5 text-center text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-primary-foreground"
                />
                <span className="text-sm text-gray-600">occurrences</span>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};
